import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button, Badge, Table, Modal, Input } from '@/components/ui';
import { procedureTypeService } from '@/services/procedureTypeService';
import type { ProcedureType, CreateProcedureTypeRequest } from '@/types';
import styles from './ProcedureTypesPage.module.css';

/**
 * Admin page for listing and managing procedure types.
 *
 * Features:
 * - List all procedure types in a table
 * - Create new procedure type via modal
 * - Edit existing procedure type via modal
 * - Toggle active/inactive status
 * - Conditional preparation instructions field
 */
export function ProcedureTypesPage() {
  const queryClient = useQueryClient();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<ProcedureType | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDuration, setFormDuration] = useState('30');
  const [formRequiresPreparation, setFormRequiresPreparation] = useState(false);
  const [formPreparationInstructions, setFormPreparationInstructions] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const {
    data: procedureTypes = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['procedure-types'],
    queryFn: () => procedureTypeService.getProcedureTypes(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateProcedureTypeRequest) =>
      procedureTypeService.createProcedureType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procedure-types'] });
      closeCreateModal();
    },
    onError: () => {
      setFormError('Erro ao criar procedimento. Tente novamente.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CreateProcedureTypeRequest;
    }) => procedureTypeService.updateProcedureType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procedure-types'] });
      closeEditModal();
    },
    onError: () => {
      setFormError('Erro ao atualizar procedimento. Tente novamente.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (procedure: ProcedureType) =>
      procedure.isActive
        ? procedureTypeService.deactivateProcedureType(procedure.id)
        : procedureTypeService.activateProcedureType(procedure.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procedure-types'] });
    },
  });

  const resetForm = useCallback(() => {
    setFormName('');
    setFormDuration('30');
    setFormRequiresPreparation(false);
    setFormPreparationInstructions('');
    setFormPrice('');
    setFormError(null);
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormError(null);
  }, []);

  const openEditModal = useCallback((procedure: ProcedureType) => {
    setFormName(procedure.name);
    setFormDuration(String(procedure.durationMinutes));
    setFormRequiresPreparation(procedure.requiresPreparation);
    setFormPreparationInstructions(procedure.preparationInstructions ?? '');
    setFormPrice(procedure.price != null ? String(procedure.price) : '');
    setFormError(null);
    setEditingProcedure(procedure);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingProcedure(null);
    setFormError(null);
  }, []);

  const buildPayload = useCallback((): CreateProcedureTypeRequest | null => {
    if (!formName.trim()) {
      setFormError('Nome é obrigatório.');
      return null;
    }
    const duration = parseInt(formDuration, 10);
    if (isNaN(duration) || duration <= 0) {
      setFormError('Duração deve ser um número positivo.');
      return null;
    }
    let price: number | null = null;
    if (formPrice.trim() !== '') {
      price = parseFloat(formPrice);
      if (isNaN(price) || price < 0) {
        setFormError('Preco deve ser um numero positivo ou vazio.');
        return null;
      }
    }
    return {
      name: formName.trim(),
      durationMinutes: duration,
      requiresPreparation: formRequiresPreparation,
      preparationInstructions: formRequiresPreparation
        ? formPreparationInstructions.trim() || null
        : null,
      price,
    };
  }, [formName, formDuration, formRequiresPreparation, formPreparationInstructions, formPrice]);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const payload = buildPayload();
      if (payload) {
        createMutation.mutate(payload);
      }
    },
    [buildPayload, createMutation],
  );

  const handleUpdate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!editingProcedure) return;
      const payload = buildPayload();
      if (payload) {
        updateMutation.mutate({ id: editingProcedure.id, data: payload });
      }
    },
    [buildPayload, editingProcedure, updateMutation],
  );

  const handleToggleActive = useCallback(
    (event: React.MouseEvent, procedure: ProcedureType) => {
      event.stopPropagation();
      toggleActiveMutation.mutate(procedure);
    },
    [toggleActiveMutation],
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent, procedure: ProcedureType) => {
      event.stopPropagation();
      openEditModal(procedure);
    },
    [openEditModal],
  );

  const columns = [
    {
      key: 'name',
      header: 'Nome',
      render: (procedure: ProcedureType) => (
        <span className={styles.procedureName}>{procedure.name}</span>
      ),
    },
    {
      key: 'duration',
      header: 'Duração',
      width: '100px',
      render: (procedure: ProcedureType) => (
        <span>{procedure.durationMinutes}min</span>
      ),
    },
    {
      key: 'preparation',
      header: 'Preparo',
      width: '120px',
      render: (procedure: ProcedureType) => (
        <Badge variant={procedure.requiresPreparation ? 'warning' : 'neutral'}>
          {procedure.requiresPreparation ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      render: (procedure: ProcedureType) => (
        <Badge variant={procedure.isActive ? 'success' : 'error'}>
          {procedure.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '200px',
      render: (procedure: ProcedureType) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleEditClick(e, procedure)}
          >
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToggleActive(e, procedure)}
            disabled={toggleActiveMutation.isPending}
          >
            {procedure.isActive ? 'Desativar' : 'Ativar'}
          </Button>
        </div>
      ),
    },
  ];

  const formFields = (
    <>
      <Input
        label="Nome"
        value={formName}
        onChange={(e) => setFormName(e.target.value)}
        placeholder="Nome do procedimento"
        required
        fullWidth
      />
      <Input
        label="Duração (minutos)"
        type="number"
        value={formDuration}
        onChange={(e) => setFormDuration(e.target.value)}
        min={5}
        max={480}
        required
        fullWidth
      />
      <Input
        label="Preco (R$)"
        type="number"
        value={formPrice}
        onChange={(e) => setFormPrice(e.target.value)}
        min={0}
        step="0.01"
        placeholder="Ex: 150.00"
        fullWidth
      />
      <div className={styles.checkboxWrapper}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={formRequiresPreparation}
            onChange={(e) => setFormRequiresPreparation(e.target.checked)}
            className={styles.checkbox}
          />
          <span>Requer preparacao do paciente</span>
        </label>
      </div>
      {formRequiresPreparation && (
        <div className={styles.textareaWrapper}>
          <label htmlFor="preparation-instructions" className={styles.textareaLabel}>
            Instrucoes de preparacao
          </label>
          <textarea
            id="preparation-instructions"
            className={styles.textarea}
            value={formPreparationInstructions}
            onChange={(e) => setFormPreparationInstructions(e.target.value)}
            rows={4}
            placeholder="Descreva as instrucoes de preparacao para o paciente..."
          />
        </div>
      )}
      {formError && (
        <p className={styles.formError} role="alert">
          {formError}
        </p>
      )}
    </>
  );

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar procedimentos. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Procedimentos</h1>
          <p className={styles.subtitle}>
            Gerencie os tipos de procedimento da clínica.
          </p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
          Novo Procedimento
        </Button>
      </header>

      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando procedimentos...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={procedureTypes}
          keyExtractor={(procedure) => procedure.id}
          emptyMessage="Nenhum procedimento cadastrado."
        />
      )}

      {/* Create modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Novo Procedimento"
        size="md"
      >
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Criar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editingProcedure !== null}
        onClose={closeEditModal}
        title="Editar Procedimento"
        size="md"
      >
        <form onSubmit={handleUpdate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeEditModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
